import React, { useState, useEffect } from 'react';
import { getCampaigns, addCampaignContact } from '../../utils/apiService';

const AddToCampaignModal = ({ onClose, onSave, alumnus }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Load campaigns from API
  useEffect(() => {
    const loadCampaigns = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getCampaigns();
        // Only show active or planned campaigns
        const activeCampaigns = data.filter(
          c => c.status === 'active' || c.status === 'planned'
        );
        setCampaigns(activeCampaigns);
        if (activeCampaigns.length > 0) {
          setSelectedCampaignId(activeCampaigns[0]._id);
        }
      } catch (err) {
        setError('Failed to load campaigns: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    loadCampaigns();
  }, []);

  // Handle campaign selection
  const handleCampaignChange = (e) => {
    setSelectedCampaignId(e.target.value);
    setError(null);
  };

  // Handle save
  const handleSave = async () => {
    if (!selectedCampaignId) {
      setError('Please select a campaign');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      const selectedCampaign = campaigns.find(c => c._id === selectedCampaignId);
      
      if (!selectedCampaign) {
        throw new Error('Selected campaign not found');
      }
      
      // Create contact object with only the required fields
      const contact = {
        alumni: alumnus._id, // Use the MongoDB _id
        status: 'pending',
        notes: ''
      };
      
      // Add contact to campaign using the API
      const result = await addCampaignContact(selectedCampaignId, contact);
      
      if (!result.success || !result.data || !result.data.campaign) {
        throw new Error('Failed to get updated campaign data from API');
      }
      
      // Pass both the updated campaign and alumnus data to the parent
      onSave({
        ...result.data.campaign,
        name: selectedCampaign.name // Ensure we have the campaign name
      }, alumnus);
      onClose();
    } catch (err) {
      console.error('Error adding to campaign:', err);
      setError('Failed to add to campaign: ' + (err.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Add to Campaign</h3>
          
          <div className="p-6">
            <div className="mb-6">
              <p className="text-gray-700 mb-2">
                Add <span className="font-semibold">{alumnus.emp_empname || alumnus.name}</span> to a campaign:
              </p>
              
              {loading ? (
                <div className="py-4 text-center text-gray-500">
                  Loading campaigns...
                </div>
              ) : error ? (
                <div className="py-4 text-center text-red-500">
                  {error}
                </div>
              ) : campaigns.length > 0 ? (
                <div className="mt-4">
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="campaign">
                    Select Campaign
                  </label>
                  <select
                    id="campaign"
                    value={selectedCampaignId}
                    onChange={handleCampaignChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {campaigns.map(campaign => (
                      <option key={campaign._id} value={campaign._id}>
                        {campaign.name} ({campaign.status}) - Target: {campaign.targetCount}
                      </option>
                    ))}
                  </select>
                  
                  {selectedCampaignId && (
                    <div className="mt-2 text-sm text-gray-600">
                      <p>Selected campaign details:</p>
                      <ul className="list-disc list-inside mt-1">
                        <li>Target Count: {campaigns.find(c => c._id === selectedCampaignId)?.targetCount}</li>
                        <li>Status: {campaigns.find(c => c._id === selectedCampaignId)?.status}</li>
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-4 text-center text-gray-500">
                  No pending or active campaigns available. Please create a campaign first.
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!selectedCampaignId || submitting || loading}
                className={`px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-700 transition-colors duration-200 ${
                  (!selectedCampaignId || submitting || loading) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {submitting ? 'Adding...' : 'Add to Campaign'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddToCampaignModal;
